import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { MutationDefinition } from '@reduxjs/toolkit/query';
import capitalize from 'lodash/capitalize';
import { useCallback } from 'react';
import { dispatch } from 'store';
import { QueryParams } from 'store/api/common/types';
import { StartExportRequest } from 'store/api/exports';
import { addExport } from 'store/reducers/exports';
import { ExportEntityType, ExportTaskResponse, exportSurfaceStorageKey, exportVariantStorageKey } from 'types/exports';
import { errorAlert, successAlert } from 'utils/alert';
import { captureListExportFailed } from 'utils/listAnalytics';
import { setLocalStorage } from 'utils/localStorage';
import { getIntl, t } from './getTranslations';
import { isNewDesignCopy } from './newDesignCopy';

// Mirrors the real `portalAPI` base query / tag set so `useAsyncExport`'s mutation generic
// matches what the per-entity export mutations are actually built from.
type PortalApiBaseQuery = BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    Record<string, unknown>,
    FetchBaseQueryMeta
>;

const MAX_EXPORT_RECORDS = 10_000;

interface UseAsyncExportProps {
    entityType: ExportEntityType;
    entityName: string;
    fileName: string;
}

export const useAsyncExport = (
    mutation: UseMutation<MutationDefinition<StartExportRequest, PortalApiBaseQuery, string, ExportTaskResponse>>,
    props: UseAsyncExportProps
) => {
    const [startExport, { isLoading }] = mutation();

    const triggerAsyncExport = useCallback(
        async (args: QueryParams, elementCount?: number, _modalArgs?: unknown, surface?: string) => {
            // Check export limit
            if (elementCount && elementCount > MAX_EXPORT_RECORDS) {
                errorAlert(
                    t('export-limit-exceeded', {
                        entityName: props.entityName,
                        limit: isNewDesignCopy()
                            ? getIntl().formatNumber(MAX_EXPORT_RECORDS)
                            : MAX_EXPORT_RECORDS.toLocaleString(),
                    })
                );
                return;
            }
            // Extract filter and sort from QueryParams
            const exportArgs: StartExportRequest = {
                filter: args.filter,
                sort: Array.isArray(args.sort) ? args.sort.join(',') : args.sort,
            };

            try {
                const result = await startExport(exportArgs).unwrap();

                // Persist the entity type so it can be recovered after a page refresh. A restored
                // task only carries the backend task type, which can be shared by several entity
                // types (e.g. the compact and detailed timecards exports); the persisted value lets
                // `mapTaskListResponseToActiveExport` pick the exact variant again.
                setLocalStorage(exportVariantStorageKey(result.taskId), props.entityType);
                // The export ribbon reports how the export ends, also after a refresh.
                if (surface) setLocalStorage(exportSurfaceStorageKey(result.taskId), surface);

                // Add to active exports in Redux state
                dispatch(
                    addExport({
                        taskId: result.taskId,
                        entityType: props.entityType,
                        status: result.status,
                        fileName: props.fileName,
                        startedAt: result.createdAt,
                        surface,
                    })
                );

                successAlert(t('export-started', { entityName: capitalize(props.entityName) }));
            } catch (error: unknown) {
                if (surface) captureListExportFailed({ surface, fileName: props.fileName });
                // Handle too many exports error
                const rtkError = error as { status?: number; data?: { message?: string } } | undefined;
                if (rtkError?.status === 400 && rtkError.data?.message?.includes('active exports')) {
                    errorAlert(rtkError.data.message);
                } else {
                    errorAlert(t('export-failed', { entityName: props.entityName }));
                }
                console.error('Export error:', error);
            }
        },
        [startExport, props]
    );

    return { isLoading, isBusy: isLoading, triggerManualDownload: triggerAsyncExport };
};
